import { Field, isString } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useModal } from '@context/ModalsContext'

import { DocumentFileUpload } from '@ui/components/FileUpload/DocumentFileUpload'
import { DoubleSidedDocumentFileUploads } from '@ui/components/FileUpload/DoubleSidedDocumentFileUploads'
import { isFileDuplicated, removeUploadedFile } from '@ui/components/FileUpload/FileUpload'
import { FileData } from '@ui/components/FileUpload/types'

import { isDoubleSidedDocument } from '@utils/uploadDocument'

import { DocumentCategorySizeDto } from '@model/DocumentCategories'
import {
  DocumentClientAreaDto,
  isDocumentClientAreaExpiredStatus,
} from '@model/DocumentClientAreaDto'

import { InformationModal } from '@shared/modals/Information.modal'

interface DocumentFileUploadsFactoryProps {
  buttonText?: string
  files?: FileData[]
  documentTypeId: number | string
  documentCategory: number
  onChange(fileData: FileData[], currentFile?: FileData): void
  onBeforeRemove?(file: FileData): void
  onRemove?(file: FileData): void

  needFileUploadRefresh?: boolean
  documents?: DocumentClientAreaDto[]
  categorySizes?: DocumentCategorySizeDto[]
}

export const DocumentFileUploadsFactory: React.FC<DocumentFileUploadsFactoryProps> = (props) => {
  const {
    files = [],
    documentTypeId,
    onChange,
    documentCategory,
    documents: apiDocuments = [],
  } = props

  const { t } = useTranslation()
  const location = useLocation()
  const { document = {} } = location.state ?? {}
  const isDocumentExpired = isDocumentClientAreaExpiredStatus(document)

  const [documents, setDocuments] = useState<FileData[]>(files)
  const duplicateInfoModal = useModal(InformationModal, {
    title: t('Upload failed'),
    secondaryLabel: t('Got It'),
    description: () => <span>{t('errors.Document is already uploaded!')}</span>,
  })

  useEffect(() => {
    setDocuments([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentTypeId])

  const handleFileUpload = (data: FileData[], currentFile?: FileData) => {
    if (!currentFile) {
      setDocuments(data)
      onChange(data)
      return
    }
    if (
      isFileDuplicated(apiDocuments, data, currentFile, false, false, documentCategory) &&
      !isDocumentExpired
    ) {
      const newDocuments = removeUploadedFile(data, currentFile)
      duplicateInfoModal.open()
      setDocuments(newDocuments)
      onChange(newDocuments)
    } else {
      setDocuments(data)
      onChange(data)
    }
  }
  const handleRemoveFile = (fileToRemove: FileData) => {
    const updated = documents.filter((f) => f.id !== fileToRemove.id)
    setDocuments(updated)
    onChange(updated)
  }

  return (
    <>
      <DocumentFileUploadsComponent
        {...props}
        files={documents}
        onChange={handleFileUpload}
        onRemove={handleRemoveFile}
        documents={apiDocuments}
      />
    </>
  )
}

const DocumentFileUploadsComponent: React.FC<DocumentFileUploadsFactoryProps> = (props) => {
  const {
    documentTypeId,
    documentCategory,
    buttonText,
    files = [],
    onChange,
    onBeforeRemove,
    needFileUploadRefresh,
    documents = [],
    categorySizes = [],
    onRemove,
  } = props

  const { t } = useTranslation()

  const documentType = getDocumentType(documentTypeId)
  const isDoubleSided = isDoubleSidedDocument(documentCategory, documentType)
  if (isDoubleSided) {
    return (
      <Field name='documents' validate={validateDocuments(t('Validation.Required'))}>
        {() => (
          <DoubleSidedDocumentFileUploads
            documentCategory={documentCategory}
            files={files}
            buttonText={buttonText}
            onUpload={onChange}
            onRemove={onRemove}
            needRefresh={needFileUploadRefresh}
            onBeforeRemove={onBeforeRemove}
            documents={documents}
            categorySizes={categorySizes}
          />
        )}
      </Field>
    )
  }

  return (
    <DocumentFileUpload
      documentCategory={documentCategory}
      files={files}
      buttonText={buttonText}
      onUpload={onChange}
      onRemove={onRemove}
      onBeforeRemove={onBeforeRemove}
      documents={documents}
      categorySizes={categorySizes}
      multiple={true}
    />
  )
}

const validateDocuments = (message: string) => (value: FileData[]) => {
  const documents = value.filter((x) => !!x?.fileName).filter((x) => !x.error)
  if (documents.length <= 1) {
    return message
  }

  return undefined
}

const getDocumentType = (documentTypeId: string | number) => {
  return isString(documentTypeId) ? Number.parseInt(documentTypeId, 10) : documentTypeId
}
